import React, { useState, useEffect, useCallback } from "react";

// ─── Helpers ───

function parseAnswer(json) {
  if (!json) {return { version: 2, items: [], notes: "" };}
  try {
    const parsed = JSON.parse(json);
    if (!parsed.version) {parsed.version = 2;}
    if (!Array.isArray(parsed.items)) {parsed.items = [];}
    if (typeof parsed.notes !== "string") {parsed.notes = "";}
    return parsed;
  } catch {
    return { version: 2, items: [], notes: "" };
  }
}

function serializeAnswer(answer) {
  const clean = { version: 2, items: [] };
  for (const item of answer.items) {
    const cleanItem = { value: item.value, percent: item.percent || 0 };
    if (item.grade) {cleanItem.grade = item.grade;}
    if (item.columns && Object.keys(item.columns).length > 0) {
      cleanItem.columns = item.columns;
    }
    clean.items.push(cleanItem);
  }
  if (answer.notes) {clean.notes = answer.notes;}
  return JSON.stringify(clean);
}

function equalizePercents(items) {
  if (items.length === 0) {return items;}
  const each = Math.floor(100 / items.length);
  const remainder = 100 - each * items.length;
  return items.map((item, i) => ({
    ...item,
    percent: each + (i < remainder ? 1 : 0),
  }));
}

function redistributeOnRemove(items, removedIndex) {
  if (items.length <= 1) {return [];}
  const remaining = items.filter((_, i) => i !== removedIndex);
  const total = remaining.reduce((s, it) => s + it.percent, 0);
  if (total === 0) {return equalizePercents(remaining);}
  return remaining.map((it) => ({
    ...it,
    percent: Math.round(it.percent / total * 100),
  }));
}

function adjustPercent(items, index, delta) {
  const copy = items.map((it) => ({ ...it }));
  copy[index].percent = Math.max(0, Math.min(100, copy[index].percent + delta));
  return copy;
}

function getTotalPercent(items) {
  return items.reduce((s, it) => s + (it.percent || 0), 0);
}

// Matches iOS DistributionValuePalette exactly.
const VALUE_PALETTE = [
  "#2563eb", "#b45309", "#7c3aed", "#0d9488", "#be123c",
  "#ca8a04", "#0369a1", "#4d7c0f", "#9333ea", "#64748b",
];

function stablePaletteIndex(normalized, count) {
  let hash = 5381;
  for (let i = 0; i < normalized.length; i++) {
    hash = (hash << 5) + hash + normalized.charCodeAt(i);
    hash = hash >>> 0; // keep unsigned 32-bit
  }
  return hash % count;
}

function getValueColor(valueColors, value, allValues) {
  // Server overrides (Go sends valueColors as [{key, colorHex}])
  if (valueColors && valueColors.length > 0) {
    const found = valueColors.find((vc) => vc.key === value);
    if (found && found.colorHex) {return found.colorHex;}
  }
  // Index in predefined values, matching iOS behavior
  if (allValues && allValues.length > 0) {
    const idx = allValues.indexOf(value);
    if (idx >= 0) {return VALUE_PALETTE[idx % VALUE_PALETTE.length];}
  }
  // Stable fallback hash by value name
  return VALUE_PALETTE[stablePaletteIndex(value.trim().toLowerCase(), VALUE_PALETTE.length)];
}

function getGradeColor(grades, gradeKey) {
  const g = grades.find((gr) => gr.key === gradeKey);
  return g ? g.colorHex : "#9ca3af";
}

// ─── Component ───

export default function FormDistributionQuestion({ targetInputId, question, initialAnswer }) {
  const [answer, setAnswer] = useState(() => parseAnswer(initialAnswer));
  const [showAdd, setShowAdd] = useState(false);

  const showPercent =
    question.subType === "percent" ||
    question.subType === "percentWithGrade" ||
    question.subType === "number";

  const hasGrades = question.grades && question.grades.length > 0;
  const hasColumns = question.columns && question.columns.length > 0;

  // Update hidden input whenever answer changes
  useEffect(() => {
    const input = document.getElementById(targetInputId);
    if (input) {
      input.value = serializeAnswer(answer);
    }
  }, [answer, targetInputId]);

  // Initialize hidden input on mount
  useEffect(() => {
    const input = document.getElementById(targetInputId);
    if (input && !input.value) {
      input.value = serializeAnswer(answer);
    }
  }, []);

  const selectedValues = new Set(answer.items.map((it) => it.value));
  const unselectedValues = (question.values || []).filter(
    (v) => !selectedValues.has(v)
  );

  const addValue = useCallback(
    (value) => {
      setAnswer((prev) => {
        if (prev.items.find((it) => it.value === value)) {return prev;}
        const newItems = showPercent
          ? equalizePercents([...prev.items, { value, percent: 0 }])
          : [...prev.items, { value, percent: 0 }];
        return { ...prev, items: newItems };
      });
      setShowAdd(false);
    },
    [showPercent]
  );

  const removeItem = useCallback(
    (index) => {
      setAnswer((prev) => {
        const newItems = showPercent
          ? redistributeOnRemove(prev.items, index)
          : prev.items.filter((_, i) => i !== index);
        return { ...prev, items: newItems };
      });
    },
    [showPercent]
  );

  const setGrade = useCallback((index, grade) => {
    setAnswer((prev) => {
      const items = prev.items.map((it, i) =>
        i === index ? { ...it, grade } : it
      );
      return { ...prev, items };
    });
  }, []);

  const setColumn = useCallback((index, colKey, colValue) => {
    setAnswer((prev) => {
      const items = prev.items.map((it, i) => {
        if (i !== index) {return it;}
        const cols = { ...it.columns || {} };
        if (colValue) {cols[colKey] = colValue;}
        else {delete cols[colKey];}
        return { ...it, columns: cols };
      });
      return { ...prev, items };
    });
  }, []);

  const setNotes = useCallback((notes) => {
    setAnswer((prev) => ({ ...prev, notes }));
  }, []);

  const totalPercent = getTotalPercent(answer.items);
  const percentValid = !showPercent || totalPercent === 100;

  return (
    <div className="tw-space-y-3">
      {/* Items */}
      {answer.items.map((item, idx) => 
        <div
          key={item.value}
          className="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-p-3"
        >
          {/* Header row */}
          <div className="tw-flex tw-items-center tw-gap-2">
            <span
              className="tw-inline-block tw-h-3 tw-w-3 tw-rounded-full tw-flex-shrink-0"
              style={{
                backgroundColor: getValueColor(
                  question.valueColors,
                  item.value,
                  question.values
                ),
              }}
            />
            <span className="tw-flex-1 tw-text-sm tw-font-medium">
              {item.value}
            </span>

            {showPercent && 
              <div className="tw-flex tw-items-center tw-gap-1">
                <button
                  type="button"
                  onClick={() =>
                    setAnswer((prev) => ({
                      ...prev,
                      items: adjustPercent(prev.items, idx, -5),
                    }))
                  }
                  className="tw-h-7 tw-w-7 tw-rounded tw-bg-gray-100 tw-text-gray-700 hover:tw-bg-gray-200 tw-text-sm"
                >
                  −
                </button>
                <span className="tw-w-10 tw-text-center tw-text-sm tw-font-medium">
                  {item.percent || 0}%
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setAnswer((prev) => ({
                      ...prev,
                      items: adjustPercent(prev.items, idx, 5),
                    }))
                  }
                  className="tw-h-7 tw-w-7 tw-rounded tw-bg-gray-100 tw-text-gray-700 hover:tw-bg-gray-200 tw-text-sm"
                >
                  +
                </button>
              </div>
            }

            {!question.requireAll && 
              <button
                type="button"
                onClick={() => removeItem(idx)}
                className="tw-h-7 tw-w-7 tw-rounded tw-text-gray-400 hover:tw-text-red-600 hover:tw-bg-red-50 tw-text-sm"
                title="Remove"
              >
                ×
              </button>
            }
          </div>

          {/* Grade picker */}
          {hasGrades && 
            <div className="tw-mt-2">
              {question.gradeStyle === "dropdown" ? 
                <select
                  className="tw-w-full tw-rounded tw-border tw-border-gray-300 tw-px-2 tw-py-1 tw-text-sm"
                  value={item.grade || ""}
                  onChange={(e) => setGrade(idx, e.target.value)}
                >
                  <option value="">Select grade…</option>
                  {question.grades.map((g) => 
                    <option key={g.key} value={g.key}>
                      {g.label}
                    </option>
                  )}
                </select>
                : 
                <div className="tw-flex tw-flex-wrap tw-gap-2">
                  {question.grades.map((g) => {
                    const selected = item.grade === g.key;
                    return (
                      <button
                        key={g.key}
                        type="button"
                        onClick={() =>
                          setGrade(idx, selected ? "" : g.key)
                        }
                        className={`tw-rounded-full tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-transition ${
                          selected
                            ? "tw-text-white"
                            : "tw-bg-gray-100 tw-text-gray-700 hover:tw-bg-gray-200"
                        }`}
                        style={selected ? { backgroundColor: g.colorHex } : {}}
                      >
                        {g.label}
                      </button>
                    );
                  })}
                </div>
              }
            </div>
          }

          {/* Columns */}
          {hasColumns && 
            <div className="tw-mt-2 tw-space-y-2">
              {question.columns.map((col) => 
                <div key={col.key} className="tw-flex tw-items-center tw-gap-2">
                  <label className="tw-text-xs tw-text-gray-500 tw-w-28">
                    {col.label}:
                  </label>
                  {col.type === "select" && 
                    <select
                      className="tw-flex-1 tw-rounded tw-border tw-border-gray-300 tw-px-2 tw-py-1 tw-text-sm"
                      value={(item.columns || {})[col.key] || ""}
                      onChange={(e) =>
                        setColumn(idx, col.key, e.target.value)
                      }
                    >
                      <option value=""></option>
                      {(col.values || []).map((v) => 
                        <option key={v} value={v}>
                          {v}
                        </option>
                      )}
                    </select>
                  }
                  {col.type === "yearselect" && 
                    <select
                      className="tw-flex-1 tw-rounded tw-border tw-border-gray-300 tw-px-2 tw-py-1 tw-text-sm"
                      value={(item.columns || {})[col.key] || ""}
                      onChange={(e) =>
                        setColumn(idx, col.key, e.target.value)
                      }
                    >
                      <option value=""></option>
                      {Array.from(
                        { length: new Date().getFullYear() - 1899 },
                        (_, i) => new Date().getFullYear() - i
                      ).map((y) => 
                        <option key={y} value={String(y)}>
                          {y}
                        </option>
                      )}
                    </select>
                  }
                  {col.type === "number" && 
                    <input
                      type="number"
                      className="tw-flex-1 tw-rounded tw-border tw-border-gray-300 tw-px-2 tw-py-1 tw-text-sm"
                      placeholder={col.placeholder || ""}
                      value={(item.columns || {})[col.key] || ""}
                      onChange={(e) =>
                        setColumn(idx, col.key, e.target.value)
                      }
                    />
                  }
                  {col.type === "text" && 
                    <input
                      type="text"
                      className="tw-flex-1 tw-rounded tw-border tw-border-gray-300 tw-px-2 tw-py-1 tw-text-sm"
                      placeholder={col.placeholder || ""}
                      value={(item.columns || {})[col.key] || ""}
                      onChange={(e) =>
                        setColumn(idx, col.key, e.target.value)
                      }
                    />
                  }
                </div>
              )}
            </div>
          }
        </div>
      )}

      {/* Distribution bar */}
      {showPercent && answer.items.length > 0 && 
        <div className="tw-rounded tw-bg-gray-50 tw-p-2">
          <div className="tw-flex tw-h-6 tw-w-full tw-overflow-hidden tw-rounded">
            {answer.items.map((item, idx) => {
              if (item.percent <= 0) {return null;}
              return (
                <div
                  key={item.value}
                  className="tw-flex tw-items-center tw-justify-center tw-text-xs tw-text-white"
                  style={{
                    width: `${item.percent}%`,
                    backgroundColor: getValueColor(
                      question.valueColors,
                      item.value,
                      question.values
                    ),
                  }}
                >
                  {item.percent >= 15 ? item.value : ""}
                </div>
              );
            })}
          </div>
          <div className="tw-mt-1 tw-flex tw-flex-wrap tw-gap-3 tw-text-xs">
            {answer.items.map((item, idx) => 
              <span key={item.value} className="tw-flex tw-items-center tw-gap-1">
                <span
                  className="tw-inline-block tw-h-2 tw-w-2 tw-rounded-full"
                  style={{
                    backgroundColor: getValueColor(
                      question.valueColors,
                      item.value,
                      question.values
                    ),
                  }}
                />
                {item.value} {item.percent}%
              </span>
            )}
          </div>
          {!percentValid && 
            <p className="tw-mt-1 tw-text-xs tw-text-amber-600">
              Total must equal 100% (currently {totalPercent}%)
            </p>
          }
        </div>
      }

      {/* Add button */}
      {!question.requireAll && unselectedValues.length > 0 && 
        <div className="tw-relative">
          <button
            type="button"
            onClick={() => setShowAdd((s) => !s)}
            className="tw-rounded tw-border tw-border-dashed tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm tw-text-gray-600 hover:tw-bg-gray-50"
          >
            + Add value
          </button>
          {showAdd && 
            <div className="tw-absolute tw-z-10 tw-mt-1 tw-w-64 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg">
              {unselectedValues.map((v) => 
                <button
                  key={v}
                  type="button"
                  onClick={() => addValue(v)}
                  className="tw-block tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm hover:tw-bg-gray-100"
                >
                  {v}
                </button>
              )}
            </div>
          }
        </div>
      }

      {/* Notes */}
      {question.allowNotes && 
        <div>
          <label className="tw-text-sm tw-text-gray-600">Notes</label>
          <textarea
            className="tw-mt-1 tw-block tw-w-full tw-rounded tw-border tw-border-gray-300 tw-px-2 tw-py-1 tw-text-sm"
            rows={2}
            value={answer.notes || ""}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      }
    </div>
  );
}
